import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Typography, Box, Paper, Switch, CircularProgress, TextField,
  FormControlLabel, FormControl, RadioGroup, Radio, Tooltip, Button,
  MenuItem, Select, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, Checkbox,
} from "@mui/material";
import PortalPicker from './PortalPicker';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { WithContext as ReactTags } from 'react-tag-input';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { useBriefing, useBriefingRuns, useUpdateBriefing, useToggleBriefing } from '../hooks/useBriefings';

const KeyCodes = { comma: 188, enter: 13 };
const delimiters = [KeyCodes.comma, KeyCodes.enter];

const TEMPLATES = [
  { value: '', label: 'Bez predloška' },
  { value: 'competitor_watch', label: 'Praćenje konkurencije', focus: 'Prati strateške promjene, lansiranja proizvoda, kadrovske promjene, M&A aktivnosti i financijske rezultate konkurenata.' },
  { value: 'crisis_monitor', label: 'Krizno praćenje', focus: 'Prati negativne medijske izvještaje, regulatorne probleme, tužbe, incidente i reakcije javnosti.' },
  { value: 'industry_trends', label: 'Trendovi u industriji', focus: 'Prati trendove u industriji, regulatorne promjene, nove tehnologije, partnerstva i tržišna kretanja.' },
  { value: 'brand_health', label: 'Zdravlje branda', focus: 'Prati medijsku pokrivenost branda, sentiment, usporedbu s konkurencijom i percepciju javnosti.' },
];

const STATUS_COLORS = {
  completed: { bg: '#e8f5e9', color: '#27ae60' },
  running: { bg: '#e3f2fd', color: '#0079bf' },
  failed: { bg: '#ffebee', color: '#e74c3c' },
  pending: { bg: '#f5f5f5', color: '#999' },
};

const SectionHeader = ({ title, tooltip }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, mt: 4 }}>
    <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50', borderBottom: '2px solid #7b1fa2', pb: 0.5, fontSize: '1rem' }}>
      {title}
    </Typography>
    {tooltip && (
      <Tooltip title={tooltip} placement="right">
        <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
      </Tooltip>
    )}
  </Box>
);

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('hr-HR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

export default function BriefingDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { showError, showSuccess } = useNotification();
  const updateBriefing = useUpdateBriefing();
  const toggleBriefingMut = useToggleBriefing();

  useEffect(() => { document.title = 'Uredi briefing | Media Scanner'; }, []);

  const { data: briefingData, isLoading, refetch } = useBriefing(id);
  const { data: runs = [] } = useBriefingRuns(id);

  const [name, setName] = useState('');
  const [tags, setTags] = useState([]);
  const [excludingTags, setExcludingTags] = useState([]);
  const [emailTags, setEmailTags] = useState([]);
  const [selectedPortals, setSelectedPortals] = useState([]);
  const [frequency, setFrequency] = useState('daily');
  const [executionDay, setExecutionDay] = useState(0);
  const [executionTime, setExecutionTime] = useState('08:00');
  const [timeSpan, setTimeSpan] = useState(24);
  const [template, setTemplate] = useState('');
  const [focusDescription, setFocusDescription] = useState('');
  const [showIrrelevantSection, setShowIrrelevantSection] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initial, setInitial] = useState(null);

  const dayLabels = ['Ponedjeljak', 'Utorak', 'Srijeda', 'Četvrtak', 'Petak', 'Subota', 'Nedjelja'];

  useEffect(() => {
    if (!briefingData) return;
    const initName = briefingData.name || '';
    const initFrequency = briefingData.frequency || 'daily';
    const initExecutionDay = briefingData.execution_day ?? 0;
    const initTime = briefingData.execution_time ? String(briefingData.execution_time).slice(0, 5) : '08:00';
    const initTimeSpan = briefingData.time_span ?? 24;
    const initTemplate = briefingData.template || '';
    const initFocus = briefingData.focus_description || '';
    const initShowIrrelevant = !!briefingData.show_irrelevant_section;
    const initTags = (briefingData.terms || []).map(t => ({ id: String(t.id ?? t.text), text: String(t.text ?? t.name ?? '') }));
    const initExcluding = (briefingData.excluding_terms || []).map(t => ({ id: String(t.id ?? t.text), text: String(t.text ?? t.name ?? '') }));
    const initPortals = (briefingData.portals || []).map(p => ({ id: p.id, name: p.name ?? p.portal_name ?? '' }));
    const initEmails = (briefingData.email_addresses || []).map(e => ({ id: e, text: e }));

    setName(initName);
    setFrequency(initFrequency);
    setExecutionDay(initExecutionDay);
    setExecutionTime(initTime);
    setTimeSpan(initTimeSpan);
    setTemplate(initTemplate);
    setFocusDescription(initFocus);
    setShowIrrelevantSection(initShowIrrelevant);
    setTags(initTags);
    setExcludingTags(initExcluding);
    setSelectedPortals(initPortals);
    setEmailTags(initEmails);

    setInitial({
      name: initName,
      frequency: initFrequency,
      executionDay: initExecutionDay,
      executionTime: initTime,
      timeSpan: initTimeSpan,
      template: initTemplate,
      focusDescription: initFocus,
      showIrrelevantSection: initShowIrrelevant,
      tags: JSON.stringify(initTags),
      excludingTags: JSON.stringify(initExcluding),
      portalIds: JSON.stringify(initPortals.map(p => p.id).sort()),
      emails: JSON.stringify(initEmails),
    });
  }, [briefingData]);

  const handleTemplateChange = (value) => {
    setTemplate(value);
    const tmpl = TEMPLATES.find(t => t.value === value);
    if (tmpl?.focus) setFocusDescription(tmpl.focus);
    else if (!value) setFocusDescription('');
  };

  const handleToggle = () => {
    toggleBriefingMut.mutate(
      { id: briefingData.id, is_active: !briefingData.is_active },
      {
        onSuccess: () => {
          showSuccess(briefingData.is_active ? 'Briefing deaktiviran' : 'Briefing aktiviran');
          refetch();
        },
        onError: () => showError('Greška pri promjeni statusa briefinga'),
      }
    );
  };

  const isFormValid = name.trim() && tags.length > 0 && selectedPortals.length > 0 && emailTags.length > 0;

  const hasChanges = initial ? (
    name !== initial.name ||
    frequency !== initial.frequency ||
    Number(executionDay) !== Number(initial.executionDay) ||
    executionTime !== initial.executionTime ||
    Number(timeSpan) !== Number(initial.timeSpan) ||
    template !== initial.template ||
    focusDescription !== initial.focusDescription ||
    showIrrelevantSection !== initial.showIrrelevantSection ||
    JSON.stringify(tags) !== initial.tags ||
    JSON.stringify(excludingTags) !== initial.excludingTags ||
    JSON.stringify(selectedPortals.map(p => p.id).sort()) !== initial.portalIds ||
    JSON.stringify(emailTags) !== initial.emails
  ) : false;

  const handleSave = async () => {
    if (!name.trim()) { showError('Naziv briefinga je obavezno polje'); return; }
    if (tags.length === 0) { showError('Dodajte barem jedan pojam'); return; }
    if (selectedPortals.length === 0) { showError('Odaberite barem jedan portal'); return; }
    if (emailTags.length === 0) { showError('Dodajte barem jednu email adresu za dostavu'); return; }

    setSaving(true);
    try {
      await updateBriefing.mutateAsync({
        id: briefingData.id,
        name: name.trim(),
        frequency,
        execution_day: frequency === 'weekly' ? executionDay : null,
        execution_time: `${executionTime}:00`,
        time_span: Number(timeSpan),
        template: template || null,
        focus_description: focusDescription.trim() || null,
        show_irrelevant_section: showIrrelevantSection,
        list_portal_ids: selectedPortals.map(p => p.id),
        list_term_names: tags.map(t => t.text),
        list_excluding_term_names: excludingTags.map(t => t.text),
        list_email_addresses: emailTags.map(t => t.text),
      });
      showSuccess('Promjene uspješno spremljene');
      refetch();
    } catch (e) {
      console.error('Update briefing failed:', e);
      const detail = e.response?.data?.detail;
      showError(detail || 'Greška pri spremanju briefinga');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      <Box sx={{ maxWidth: 800, mx: 'auto', px: 2, py: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50', mb: 3 }}>
          Uredi briefing
        </Typography>

        <Paper sx={{ p: 3, borderRadius: 2 }}>
          {/* Name & Status */}
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50', mb: 0.5 }}>Naziv</Typography>
          <TextField
            fullWidth value={name} onChange={(e) => setName(e.target.value)}
            error={!name.trim()} helperText={!name.trim() ? 'Naziv je obavezno polje' : ''}
            size="small" sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50' }}>Status:</Typography>
            <Typography variant="caption" sx={{
              px: 1, py: 0.25, borderRadius: 1, fontWeight: 600,
              backgroundColor: briefingData?.is_active ? '#e8f5e9' : '#f5f5f5',
              color: briefingData?.is_active ? '#27ae60' : '#999',
            }}>
              {briefingData?.is_active ? 'Aktivan' : 'Neaktivan'}
            </Typography>
            <Switch size="small" checked={!!briefingData?.is_active} onChange={handleToggle} />
          </Box>

          {/* Template */}
          <SectionHeader title="Predložak" tooltip="Predložak automatski popunjava opis fokusa. Možete ga naknadno prilagoditi." />
          <Select value={template} onChange={(e) => handleTemplateChange(e.target.value)} size="small" fullWidth sx={{ mb: 2 }}>
            {TEMPLATES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </Select>

          {/* Focus description */}
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50', mb: 0.5 }}>Opis fokusa</Typography>
          <TextField
            fullWidth multiline rows={3} placeholder="Opišite što AI treba prioritizirati u briefingu..."
            value={focusDescription} onChange={(e) => setFocusDescription(e.target.value)} size="small" sx={{ mb: 1 }}
          />
          <Typography variant="caption" color="text.secondary">
            AI će koristiti ovaj opis za usmjeravanje sažetka. Npr. "Prati kadrovske promjene i M&A aktivnosti."
          </Typography>

          {/* Show irrelevant section */}
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={<Checkbox checked={showIrrelevantSection} onChange={(e) => setShowIrrelevantSection(e.target.checked)} size="small" />}
              label={<Typography variant="body2">Prikaži manje relevantne članke</Typography>}
            />
            <Tooltip title="Kada je uključeno, članci koje AI procijeni kao manje relevantne za vaše pojmove bit će prikazani u zasebnoj sekciji na kraju brifinga. Kada je isključeno, takvi članci neće biti prikazani." placement="right">
              <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary', ml: -1 }} />
            </Tooltip>
          </Box>

          {/* Schedule */}
          <SectionHeader title="Učestalost" />
          <FormControl>
            <RadioGroup row value={frequency} onChange={(e) => setFrequency(e.target.value)}>
              <FormControlLabel value="daily" control={<Radio />} label="Svakodnevno" />
              <FormControlLabel value="weekly" control={<Radio />} label="Tjedno" />
            </RadioGroup>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 3, mt: 2, flexWrap: 'wrap' }}>
            {frequency === 'weekly' && (
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600, color: '#2c3e50' }}>Dan u tjednu</Typography>
                <Select value={executionDay} onChange={(e) => setExecutionDay(e.target.value)} size="small" sx={{ minWidth: 160 }}>
                  {dayLabels.map((label, i) => <MenuItem key={i} value={i}>{label}</MenuItem>)}
                </Select>
              </Box>
            )}
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600, color: '#2c3e50' }}>Vrijeme pokretanja</Typography>
              <TextField type="time" value={executionTime} onChange={(e) => setExecutionTime(e.target.value)} inputProps={{ step: 300 }} size="small" />
            </Box>
            <Box>
              <Tooltip title="Koliko sati unazad treba analizirati članke" placement="right">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50' }}>Pokrivenost (sati)</Typography>
                  <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                </Box>
              </Tooltip>
              <TextField type="number" value={timeSpan} onChange={(e) => setTimeSpan(e.target.value)} inputProps={{ min: 1, max: 168 }} size="small" sx={{ width: 120 }} />
            </Box>
          </Box>

          {/* Terms */}
          <SectionHeader title="Pojmovi" />
          <ReactTags tags={tags} suggestions={[]} delimiters={delimiters}
            handleDelete={(i) => setTags(tags.filter((_, idx) => idx !== i))}
            handleAddition={(tag) => setTags([...tags, { id: String(tag.id ?? tag.text), text: String(tag.text) }])}
            handleDrag={(tag, currPos, newPos) => { const n = tags.slice(); n.splice(currPos, 1); n.splice(newPos, 0, tag); setTags(n); }}
            handleTagClick={() => {}} inputFieldPosition="bottom" placeholder="Unesi pojam, zatim zarez ili Enter" autocomplete editable
          />

          {/* Excluding terms */}
          <SectionHeader title="Isključujući pojmovi" tooltip="Članci koji sadrže ove pojmove neće biti uključeni u briefing" />
          <ReactTags tags={excludingTags} suggestions={[]} delimiters={delimiters}
            handleDelete={(i) => setExcludingTags(excludingTags.filter((_, idx) => idx !== i))}
            handleAddition={(tag) => setExcludingTags([...excludingTags, { id: String(tag.id ?? tag.text), text: String(tag.text) }])}
            handleTagClick={() => {}} inputFieldPosition="bottom" placeholder="Unesi pojam za isključivanje" autocomplete editable
          />

          {/* Portals */}
          <SectionHeader title="Portali" />
          <PortalPicker value={selectedPortals} onChange={setSelectedPortals} showChips={false} autoDetectForeign />

          {/* Email */}
          <SectionHeader title="Email adrese za dostavu" />
          <ReactTags tags={emailTags} suggestions={[]} delimiters={delimiters}
            handleDelete={(i) => setEmailTags(emailTags.filter((_, idx) => idx !== i))}
            handleAddition={(tag) => setEmailTags([...emailTags, tag])}
            handleTagClick={() => {}} inputFieldPosition="bottom" placeholder="Unesi email adresu" autocomplete editable
          />

          {/* Save */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 4, pt: 3, borderTop: '1px solid #eee' }}>
            <Button variant="outlined" onClick={() => navigate('/briefings')}>Natrag na listu</Button>
            <Button variant="contained" onClick={handleSave} disabled={saving || !isFormValid || !hasChanges}
              startIcon={saving ? <CircularProgress size={16} /> : null}
              sx={{ backgroundColor: '#7b1fa2', '&:hover': { backgroundColor: '#6a1b9a' } }}
            >
              {saving ? 'Spremanje...' : 'Spremi promjene'}
            </Button>
          </Box>
        </Paper>

        {/* Recent Runs */}
        {runs.length > 0 && (
          <>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50', mt: 4, mb: 1 }}>
              Zadnja pokretanja
            </Typography>
            <Paper sx={{ overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Vrijeme</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Članci</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Klasteri</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {runs.slice(0, 10).map((run) => {
                    const sc = STATUS_COLORS[run.status] || STATUS_COLORS.pending;
                    return (
                      <TableRow key={run.id}>
                        <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{formatDate(run.timestamp_started)}</TableCell>
                        <TableCell>
                          <Chip label={run.status} size="small" sx={{ backgroundColor: sc.bg, color: sc.color, fontWeight: 600, fontSize: 11 }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 13 }}>{run.articles_found ?? '—'}</TableCell>
                        <TableCell sx={{ fontSize: 13 }}>{run.clusters_formed ?? '—'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          </>
        )}
      </Box>
    </Box>
  );
}
